import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { validateMobile, cleanMobile, validateDOB, calculateAge, validateAge } from "@/lib/validation";

// Owns the "Personal Details" / "Medical History" edit-mode form state for the
// Patient Profile page — keeps `form` in sync with the freshly-loaded `patient`,
// and exposes save/cancel handlers so PatientProfileContent doesn't need to.
export function usePatientEditForm({ patient, patientId, onSaved }) {
  const [form, setForm] = useState({});
  const [editingPersonal, setEditingPersonal] = useState(false);
  const [editingMedical, setEditingMedical] = useState(false);
  const [editTouched, setEditTouched] = useState({});

  useEffect(() => {
    if (patient) setForm(patient);
  }, [patient]);

  const editFieldErrors = {
    mobile: validateMobile(form.mobile),
    dob: validateDOB(form.dob),
    age: validateAge(form.age),
  };
  const editIsValid = Object.values(editFieldErrors).every((e) => !e);

  const setEditField = (f, v) => {
    if (f === "dob") {
      const age = calculateAge(v);
      setForm((s) => ({ ...s, dob: v, age: age === "" ? s.age : String(age) }));
    } else {
      setForm((s) => ({ ...s, [f]: v }));
    }
  };

  const cancelEdit = (fields) => {
    setForm((s) => {
      const next = { ...s };
      fields.forEach((f) => { next[f] = patient[f]; });
      return next;
    });
  };

  const saveEdit = async (section) => {
    if (section === "personal") {
      setEditTouched((t) => ({ ...t, mobile: true, dob: true, age: true }));
      if (!editIsValid) return;
    }
    try {
      await api.put(`/patients/${patientId}`, { ...form, mobile: cleanMobile(form.mobile) });
      toast.success("Patient updated");
      if (section === "personal") {
        setEditingPersonal(false);
        setEditTouched({});
      } else {
        setEditingMedical(false);
      }
      onSaved();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  return {
    form, setForm,
    editingPersonal, setEditingPersonal,
    editingMedical, setEditingMedical,
    editTouched, setEditTouched,
    editFieldErrors, editIsValid,
    setEditField, saveEdit, cancelEdit,
  };
}
